/**
 * ProviderPolicyPanel Component
 *
 * Modal for viewing and editing the model discovery and activation policy
 * for a platform provider. Aligned with backend ProviderPolicy schema.
 */

import { AppModal } from '@/components/ui/app-modal';
import { useEffect, useState } from 'react';
import { X, Loader2, Save, RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  useProviderPolicy,
  useUpdateProviderPolicy,
} from '../hooks/usePlatformProviders';
import type { PlatformProvider, ProviderPolicyUpdate } from '../types';
import { ProviderIcon } from './ProviderIcons';

// =============================================================================
// Helpers
// =============================================================================

/** Parse a comma-separated string into a trimmed array (empty strings filtered) */
function parseList(value: string): string[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Join an array into a comma-separated display string */
function joinList(arr: string[]): string {
  return arr.join(', ');
}

// =============================================================================
// Component
// =============================================================================

interface ProviderPolicyPanelProps {
  provider: PlatformProvider;
  onClose: () => void;
}

export function ProviderPolicyPanel({ provider, onClose }: ProviderPolicyPanelProps) {
  const { data: policy, isLoading } = useProviderPolicy(provider.id);
  const updateMutation = useUpdateProviderPolicy();

  // Local form state — editable text fields for arrays
  const [lifecycleStatuses, setLifecycleStatuses] = useState('');
  const [excludedPrefixes, setExcludedPrefixes] = useState('');
  const [excludedNames, setExcludedNames] = useState('');
  const [requiredCapabilities, setRequiredCapabilities] = useState('');
  const [allowedCategories, setAllowedCategories] = useState('');
  const [activationMode, setActivationMode] = useState<string>('allowlist');
  const [requiredSkuTypes, setRequiredSkuTypes] = useState('');
  const [inactivePrefixes, setInactivePrefixes] = useState('');
  const [activeModels, setActiveModels] = useState('');
  const [defaultModels, setDefaultModels] = useState('');
  const [discoveryInterval, setDiscoveryInterval] = useState(3600);

  /** Populate all form fields from the given policy object */
  const populateForm = (p: NonNullable<typeof policy>) => {
    setLifecycleStatuses(joinList(p.allowed_lifecycle_statuses ?? []));
    setExcludedPrefixes(joinList(p.excluded_model_prefixes ?? []));
    setExcludedNames(joinList(p.excluded_model_names ?? []));
    setRequiredCapabilities(joinList(p.required_capabilities ?? []));
    setAllowedCategories(joinList(p.allowed_categories ?? []));
    setActivationMode(p.activation_mode ?? 'denylist');
    setRequiredSkuTypes(joinList(p.required_sku_types ?? []));
    setInactivePrefixes(joinList(p.inactive_model_prefixes ?? []));
    setActiveModels(joinList(p.active_models ?? []));
    setDefaultModels(
      Object.entries(p.default_model_by_category ?? {})
        .map(([k, v]) => `${k}:${v}`)
        .join(', ')
    );
    setDiscoveryInterval(p.discovery_interval_seconds ?? 3600);
  };

  // Populate form when policy loads
  useEffect(() => {
    if (policy) populateForm(policy);
  }, [policy]);

  const handleSave = async () => {
    // Parse default_model_by_category from "category:model_id, ..." format
    const defaultMap: Record<string, string> = {};
    parseList(defaultModels).forEach((entry) => {
      const [key, ...rest] = entry.split(':');
      if (key && rest.length) {
        defaultMap[key.trim()] = rest.join(':').trim();
      }
    });

    const payload: ProviderPolicyUpdate = {
      allowed_lifecycle_statuses: parseList(lifecycleStatuses),
      excluded_model_prefixes: parseList(excludedPrefixes),
      excluded_model_names: parseList(excludedNames),
      required_capabilities: parseList(requiredCapabilities),
      allowed_categories: parseList(allowedCategories),
      activation_mode: activationMode,
      required_sku_types: parseList(requiredSkuTypes),
      inactive_model_prefixes: parseList(inactivePrefixes),
      active_models: parseList(activeModels),
      default_model_by_category: defaultMap,
      discovery_interval_seconds: discoveryInterval,
    };

    await updateMutation.mutateAsync({
      providerId: provider.id,
      data: payload,
    });
  };

  const handleReset = () => {
    if (policy) populateForm(policy);
  };

  return (
    <AppModal
      onClose={onClose}
      ariaLabel={`${provider.display_name} — Policy`}
      testId="provider-policy-modal"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <ProviderIcon providerType={provider.provider_type} size={24} />
              {provider.display_name} — Policy
            </h2>
            <p className="text-sm text-gray-500">
              Model discovery and activation rules
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : !policy ? (
            <p className="text-center py-8 text-gray-500">
              No policy found. It will be created with defaults.
            </p>
          ) : (
            <>
              {/* Lifecycle filter */}
              <FieldGroup
                label="Allowed Lifecycle Statuses"
                hint="Comma-separated. Only models with these lifecycle statuses will be discovered."
              >
                <Input
                  value={lifecycleStatuses}
                  onChange={(e) => setLifecycleStatuses(e.target.value)}
                  placeholder="Generally Available, Preview"
                />
              </FieldGroup>

              {/* Exclusions */}
              <FieldGroup
                label="Excluded Model Prefixes"
                hint="Comma-separated prefixes. Models whose ID starts with these will be excluded."
              >
                <Input
                  value={excludedPrefixes}
                  onChange={(e) => setExcludedPrefixes(e.target.value)}
                  placeholder="ft-, custom-"
                />
              </FieldGroup>

              <FieldGroup
                label="Excluded Model Names"
                hint="Comma-separated exact model IDs to exclude."
              >
                <Input
                  value={excludedNames}
                  onChange={(e) => setExcludedNames(e.target.value)}
                  placeholder="gpt-35-turbo, davinci"
                />
              </FieldGroup>

              {/* Capabilities */}
              <FieldGroup
                label="Required Capabilities (any)"
                hint="Comma-separated. Models must have AT LEAST ONE of these capabilities (e.g., chatCompletion, embeddings)."
              >
                <Input
                  value={requiredCapabilities}
                  onChange={(e) => setRequiredCapabilities(e.target.value)}
                  placeholder="chat, inference"
                />
              </FieldGroup>

              <FieldGroup
                label="Required SKU Types"
                hint="Comma-separated. Only models with these SKU types (e.g., GlobalStandard)."
              >
                <Input
                  value={requiredSkuTypes}
                  onChange={(e) => setRequiredSkuTypes(e.target.value)}
                  placeholder="GlobalStandard"
                />
              </FieldGroup>

              {/* Categories */}
              <FieldGroup
                label="Allowed Categories"
                hint="Comma-separated. Model categories allowed: main, router, tools, embedding."
              >
                <Input
                  value={allowedCategories}
                  onChange={(e) => setAllowedCategories(e.target.value)}
                  placeholder="main, router, tools, embedding"
                />
              </FieldGroup>

              {/* Activation mode */}
              <FieldGroup
                label="Activation Mode"
                hint="allowlist = only listed models are active. denylist = all except listed are active."
              >
                <select
                  value={activationMode}
                  onChange={(e) => setActivationMode(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                >
                  <option value="allowlist">Allowlist (only listed models active)</option>
                  <option value="denylist">Denylist (all except listed are active)</option>
                </select>
              </FieldGroup>

              {activationMode === 'allowlist' ? (
                <FieldGroup
                  label="Active Models (allowlist)"
                  hint="Comma-separated model IDs that should be active."
                >
                  <textarea
                    value={activeModels}
                    onChange={(e) => setActiveModels(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 text-sm"
                    placeholder="Claude-4-Sonnet, Claude-4-Haiku"
                  />
                </FieldGroup>
              ) : (
                <FieldGroup
                  label="Inactive Model Prefixes (denylist)"
                  hint="Comma-separated prefixes. Models matching these will be inactive."
                >
                  <Input
                    value={inactivePrefixes}
                    onChange={(e) => setInactivePrefixes(e.target.value)}
                    placeholder="deprecated-, legacy-"
                  />
                </FieldGroup>
              )}

              {/* Default models */}
              <FieldGroup
                label="Default Model per Category"
                hint='Format: "category:model_id". Comma-separated. E.g., "main:Claude-4-Sonnet, embedding:voyage-3-large"'
              >
                <textarea
                  value={defaultModels}
                  onChange={(e) => setDefaultModels(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 text-sm"
                  placeholder="main:Claude-4-Sonnet, router:Claude-4-Haiku, tools:Claude-4-Sonnet"
                />
              </FieldGroup>

              {/* Discovery interval */}
              <FieldGroup
                label="Discovery Interval (seconds)"
                hint="How often to re-discover models from the provider API."
              >
                <Input
                  type="number"
                  value={discoveryInterval}
                  onChange={(e) => setDiscoveryInterval(Number(e.target.value))}
                  min={60}
                  placeholder="3600"
                />
              </FieldGroup>
            </>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between p-4 border-t dark:border-gray-700">
          <Button variant="outline" onClick={handleReset} disabled={isLoading}>
            <RotateCcw className="w-4 h-4 mr-1" />
            Reset
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={updateMutation.isPending || isLoading}
            >
              {updateMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-1" />
              )}
              Save Policy
            </Button>
          </div>
        </div>

        {/* Success/error banner */}
        {updateMutation.isSuccess && (
          <div className="mx-4 mb-4 p-2 rounded-lg text-sm bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300">
            Policy saved successfully.
          </div>
        )}
        {updateMutation.isError && (
          <div className="mx-4 mb-4 p-2 rounded-lg text-sm bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300">
            Failed to save policy: {(updateMutation.error as Error)?.message || 'Unknown error'}
          </div>
        )}
      </div>
    </AppModal>
  );
}

// =============================================================================
// Field Group Helper
// =============================================================================

function FieldGroup({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </label>
      {children}
      {hint && (
        <p className="text-xs text-gray-400 mt-1">{hint}</p>
      )}
    </div>
  );
}
